import { useState, useEffect, useRef } from 'react';
import { hotNumbersApi } from '../../api/hotNumbers';
import { gamesApi } from '../../api/games';
import { useAuth } from '../../context/AuthContext';
import { format } from 'date-fns';
import HeaderWithIcon from '../../components/common/HeaderWithIcon';
import { useNavigate } from 'react-router-dom';
import { Button, TextInput, Select, Dropdown } from 'flowbite-react';
import { Calendar, Download, FileText, Filter } from 'lucide-react';
import { useBottomNav } from '../../context/BottomNavContext';
import { jsPDF } from 'jspdf';

export default function HotNumbers() {
  const { tokens } = useAuth();
  const navigate = useNavigate();
  const { isIOS } = useBottomNav();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [games, setGames] = useState([]);
  const [hotNumbers, setHotNumbers] = useState({});
  const [drawTimes, setDrawTimes] = useState([]);
  const [gamesLoaded, setGamesLoaded] = useState(false);

  // Search params
  const [filters, setFilters] = useState({
    drawDate: format(new Date(), 'yyyy-MM-dd'),
    gameId: 1,
    drawTime: '',
    combination: '',
    page: 1,
    limit: 20,
  });

  const [displayedDate, setDisplayedDate] = useState(
    format(new Date(), 'MM/dd/yyyy')
  );
  const [showResults, setShowResults] = useState(false);
  const [viewTabular, setViewTabular] = useState(true);

  // Timestamp captured when hot numbers data is loaded (on filter/fetch)
  const dataLoadedAtRef = useRef(null);
  const [dataLoadedAt, setDataLoadedAt] = useState(null);
  const dataLoadedAtFormatted = dataLoadedAt
    ? format(dataLoadedAt, 'MMM dd, yyyy hh:mm:ss a')
    : '';

  // Effect to load games on mount
  useEffect(() => {
    fetchGames();
  }, []);

  // Effect to fetch hot numbers once games and initial filters are set
  useEffect(() => {
    if (gamesLoaded && filters.drawTime) {
      fetchHotNumbers();
    }
  }, [gamesLoaded, filters.drawTime]);

  // Effect to update draw times when game selection changes
  useEffect(() => {
    if (games.length > 0) {
      const selectedGame = games.find(
        (game) => game.id === Number(filters.gameId)
      );
      if (
        selectedGame &&
        selectedGame.otherSettings &&
        selectedGame.otherSettings.drawTime
      ) {
        const newDrawTimes = selectedGame.otherSettings.drawTime;
        setDrawTimes(newDrawTimes);

        // Reset draw time to first available when game changes
        // or if current drawTime is not valid for the new game
        if (newDrawTimes.length > 0) {
          const isCurrentDrawTimeValid = newDrawTimes.includes(filters.drawTime);
          if (!filters.drawTime || !isCurrentDrawTimeValid) {
            setFilters((prev) => ({
              ...prev,
              drawTime: newDrawTimes[0],
            }));
          }
        }
      }
    }
  }, [filters.gameId, games]);

  const fetchGames = async () => {
    try {
      setLoading(true);
      const { data } = await gamesApi.getAllGames(tokens.access.token);
      setGames(data);

      // Set default game and initialize draw times
      if (data && data.length > 0) {
        const defaultGame = data[0];

        setFilters((prev) => ({
          ...prev,
          gameId: defaultGame.id,
        }));

        if (defaultGame.otherSettings && defaultGame.otherSettings.drawTime) {
          setDrawTimes(defaultGame.otherSettings.drawTime);

          if (defaultGame.otherSettings.drawTime.length > 0) {
            setFilters((prev) => ({
              ...prev,
              drawTime: defaultGame.otherSettings.drawTime[0],
            }));
          }
        }

        // Mark games as loaded to trigger hot numbers fetch
        setGamesLoaded(true);
      } else {
        setLoading(false);
      }
    } catch (err) {
      console.error('Failed to fetch games:', err);
      setError('Failed to load games. Please try again.');
      setLoading(false);
    }
  };

  const fetchHotNumbers = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('Fetching hot numbers with params:', filters);

      const data = await hotNumbersApi.getHotNumbers(
        tokens.access.token,
        filters
      );
      const loadedAt = new Date();
      dataLoadedAtRef.current = loadedAt;
      setDataLoadedAt(loadedAt);
      setHotNumbers(data);
      setShowResults(true);
      setLoading(false);
      return data;
    } catch (err) {
      setError('Failed to fetch hot numbers. Please try again.');
      setLoading(false);
      console.error(err);
      throw err;
    }
  };

  const handleDateChange = (e) => {
    const { value } = e.target;
    // Parse and format the date
    try {
      const date = new Date(value);
      setFilters((prev) => ({
        ...prev,
        drawDate: format(date, 'yyyy-MM-dd'),
      }));
      setDisplayedDate(format(date, 'MM/dd/yyyy'));
    } catch (err) {
      console.error('Invalid date:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFilter = () => {
    fetchHotNumbers();
  };

  const getGroupedCombinationsFromData = (data) => {
    const grouped = {};
    for (const key in data) {
      const firstDigit = key.charAt(0);
      if (!grouped[firstDigit]) {
        grouped[firstDigit] = [];
      }
      grouped[firstDigit].push(data[key]);
    }

    Object.keys(grouped).forEach((digit) => {
      grouped[digit].sort((a, b) =>
        String(a.combination).localeCompare(String(b.combination))
      );
    });

    return grouped;
  };

  const generateTabularPdf = (data) => {
    const pdf = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4',
    });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 10;
    const rowHeight = 4;
    const pageHeaderHeight = 24;
    const bandHeaderHeight = 5;

    const combiColWidth = 11;
    const targetColWidth = 10;
    const rambleColWidth = 9;
    const blockWidth = combiColWidth + targetColWidth + rambleColWidth;
    const availableWidth = pageWidth - margin * 2;
    const digitsPerRow = Math.max(1, Math.floor(availableWidth / blockWidth));

    const drawDateFormatted = format(
      new Date(filters.drawDate),
      'EEE, MMM dd, yyyy'
    );
    const dataLoaded = dataLoadedAtRef.current
      ? format(dataLoadedAtRef.current, 'MMM dd, yyyy hh:mm:ss a')
      : '';
    const generatedAt = format(new Date(), 'MMM dd, yyyy hh:mm:ss a');
    const gameName = getSelectedGameName();
    const grouped = getGroupedCombinationsFromData(data);
    const digits = Object.keys(grouped).sort();
    const maxRows = digits.reduce(
      (max, digit) => Math.max(max, grouped[digit].length),
      0
    );

    const digitBands = [];
    for (let i = 0; i < digits.length; i += digitsPerRow) {
      digitBands.push(digits.slice(i, i + digitsPerRow));
    }

    const rowsPerPage = Math.max(
      1,
      Math.floor(
        (pageHeight -
          margin -
          pageHeaderHeight -
          digitBands.length * bandHeaderHeight -
          4) /
          rowHeight
      )
    );

    const drawPageHeader = () => {
      let y = margin;
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.text('BET TABULATION', pageWidth / 2, y, { align: 'center' });
      y += 6;

      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      pdf.text(
        `Draw Date/Time: ${drawDateFormatted} ${filters.drawTime}`,
        pageWidth / 2,
        y,
        { align: 'center' }
      );
      y += 4;
      pdf.text(`Game: ${gameName}`, pageWidth / 2, y, { align: 'center' });
      y += 4;
      pdf.setFontSize(7);
      pdf.setTextColor(120);
      pdf.text(`Data Loaded: ${dataLoaded}`, pageWidth / 2, y, {
        align: 'center',
      });
      y += 3.5;
      pdf.text(`Generated: ${generatedAt}`, pageWidth / 2, y, {
        align: 'center',
      });
      pdf.setTextColor(0);
      return y + 5;
    };

    const drawDigitBlock = (digit, x, startY, rowStart, rows) => {
      let colY = startY;

      pdf.setFontSize(7);
      pdf.setFont('helvetica', 'bold');
      pdf.setFillColor(235, 240, 250);
      pdf.rect(x, colY - 3.5, blockWidth, bandHeaderHeight, 'F');
      pdf.setDrawColor(180);
      pdf.rect(x, colY - 3.5, blockWidth, bandHeaderHeight);
      pdf.text(digit, x + 1, colY);
      pdf.text('T', x + combiColWidth + 1, colY);
      pdf.text('R', x + combiColWidth + targetColWidth + 1, colY);
      colY += 4;

      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(6.5);
      for (
        let i = rowStart;
        i < Math.min(rowStart + rowsPerPage, rows.length);
        i += 1
      ) {
        const row = rows[i];
        pdf.text(String(row.combination), x + 1, colY);
        pdf.text(
          Number(row.target || 0).toFixed(2),
          x + combiColWidth + targetColWidth - 1,
          colY,
          { align: 'right' }
        );
        pdf.text(
          Number(row.ramble || 0).toFixed(2),
          x + blockWidth - 1,
          colY,
          { align: 'right' }
        );
        colY += rowHeight;
      }

      return colY;
    };

    if (maxRows === 0) {
      drawPageHeader();
      pdf.text('No hot numbers data found', pageWidth / 2, margin + 30, {
        align: 'center',
      });
      pdf.save(`hot-numbers-${filters.drawDate}.pdf`);
      return;
    }

    let rowStart = 0;
    let pageNum = 0;

    while (rowStart < maxRows) {
      if (pageNum > 0) {
        pdf.addPage();
      }
      pageNum += 1;

      let y = drawPageHeader();

      digitBands.forEach((bandDigits) => {
        const bandTop = y;
        let bandBottom = bandTop;

        bandDigits.forEach((digit, colIndex) => {
          const x = margin + colIndex * blockWidth;
          const blockBottom = drawDigitBlock(
            digit,
            x,
            bandTop,
            rowStart,
            grouped[digit]
          );
          bandBottom = Math.max(bandBottom, blockBottom);
        });

        y = bandBottom + 3;
      });

      rowStart += rowsPerPage;
    }

    pdf.save(`hot-numbers-${filters.drawDate}.pdf`);
  };

  const generateTablePdf = (data) => {
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 12;
    const rowHeight = 5;
    let y = margin;

    const drawDateFormatted = format(
      new Date(filters.drawDate),
      'EEE, MMM dd, yyyy'
    );
    const dataLoaded = dataLoadedAtRef.current
      ? format(dataLoadedAtRef.current, 'MMM dd, yyyy hh:mm:ss a')
      : '';
    const generatedAt = format(new Date(), 'MMM dd, yyyy hh:mm:ss a');
    const gameName = getSelectedGameName();
    const bets = Object.entries(data).sort((a, b) => b[1].total - a[1].total);

    const drawHeader = () => {
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('BET TABULATION', pageWidth / 2, y, { align: 'center' });
      y += 7;

      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text(
        `Draw Date/Time: ${drawDateFormatted} ${filters.drawTime}`,
        pageWidth / 2,
        y,
        { align: 'center' }
      );
      y += 5;
      pdf.text(`Game: ${gameName}`, pageWidth / 2, y, { align: 'center' });
      y += 5;
      pdf.setFontSize(8);
      pdf.setTextColor(120);
      pdf.text(`Data Loaded: ${dataLoaded}`, pageWidth / 2, y, {
        align: 'center',
      });
      y += 4;
      pdf.text(`Generated: ${generatedAt}`, pageWidth / 2, y, {
        align: 'center',
      });
      pdf.setTextColor(0);
      y += 8;
    };

    const drawColumnHeaders = () => {
      const columns = [
        { label: 'Combi', x: margin, align: 'left' },
        { label: 'Total', x: pageWidth - margin, align: 'right' },
        { label: 'Target', x: pageWidth - margin - 35, align: 'right' },
        { label: 'Ramble', x: pageWidth - margin - 70, align: 'right' },
      ];

      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'bold');
      columns.forEach(({ label, x, align }) => {
        pdf.text(label, x, y, { align });
      });
      y += 2;
      pdf.setDrawColor(200);
      pdf.line(margin, y, pageWidth - margin, y);
      y += 4;
      pdf.setFont('helvetica', 'normal');
    };

    drawHeader();
    drawColumnHeaders();

    if (bets.length === 0) {
      pdf.text('No hot numbers data found', pageWidth / 2, y, { align: 'center' });
    } else {
      bets.forEach(([, row]) => {
        if (y > pageHeight - margin) {
          pdf.addPage();
          y = margin;
          drawColumnHeaders();
        }

        const total = Number(row.total) || 0;
        const target = Number(row.target) || 0;
        const ramble = Number(row.ramble) || 0;

        pdf.setFontSize(9);
        pdf.text(String(row.combination), margin, y);
        pdf.text(total.toFixed(2), pageWidth - margin, y, { align: 'right' });
        pdf.text(target.toFixed(2), pageWidth - margin - 35, y, {
          align: 'right',
        });
        pdf.text(ramble.toFixed(2), pageWidth - margin - 70, y, {
          align: 'right',
        });
        y += rowHeight;
      });
    }

    pdf.save(`hot-numbers-${filters.drawDate}.pdf`);
  };

  const downloadPDF = async () => {
    try {
      const data = await fetchHotNumbers();

      if (!data || Object.keys(data).length === 0) {
        alert('No hot numbers data to export');
        return;
      }

      generatePDF(data);
    } catch (err) {
      console.error('Failed to export PDF:', err);
      alert('Failed to refresh data before PDF export. Please try again.');
    }
  };

  const generatePDF = (data) => {
    if (viewTabular) {
      generateTabularPdf(data);
      return;
    }

    generateTablePdf(data);
  };

  const downloadCSV = async () => {
    try {
      // Always re-fetch so export matches current DB (not stale in-memory data)
      const data = await fetchHotNumbers();

      if (!data || Object.keys(data).length === 0) {
        alert('No hot numbers data to export');
        return;
      }

      generateCSV(data);
    } catch (err) {
      console.error('Failed to export CSV:', err);
      alert('Failed to refresh data before CSV export. Please try again.');
    }
  };

  const generateCSV = (data = hotNumbers) => {
    // Convert object to array for easier processing
    const hotNumbersArray = Object.values(data);

    // Sort by total (highest first)
    hotNumbersArray.sort((a, b) => b.total - a.total);

    // Create CSV header row
    let csvContent = `Date:, ${filters.drawDate}, Time:, ${filters.drawTime}\n`;
    csvContent += `Data Loaded:, ${
      dataLoadedAtRef.current
        ? format(dataLoadedAtRef.current, 'MMM dd, yyyy hh:mm:ss a')
        : ''
    }\n`;
    csvContent += `Generated:, ${format(new Date(), 'MMM dd, yyyy hh:mm:ss a')}\n`;
    csvContent += 'Combination,Total,Target,Ramble\n';

    // Add data rows
    hotNumbersArray.forEach((item) => {
      csvContent += `${item.combination},${Number(item.total || 0).toFixed(
        2
      )},${Number(item.target || 0).toFixed(2)},${Number(item.ramble || 0).toFixed(2)}\n`;
    });

    // Create a blob and download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `hot-numbers-${filters.drawDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getSelectedGameName = () => {
    const game = games.find((g) => g.id === Number(filters.gameId));
    return game ? game.name : '';
  };

  const getGroupedCombinations = () => {
    const groupedCombinations = {};
    for (const key in hotNumbers) {
      const firstDigit = key.charAt(0);
      if (!groupedCombinations[firstDigit]) {
        groupedCombinations[firstDigit] = [];
      }
      groupedCombinations[firstDigit].push(hotNumbers[key]);
    }
    return groupedCombinations;
  };

  const getBetsArray = () => {
    const dataArray = Object.entries(hotNumbers);
    return dataArray.sort((a, b) => b[1].total - a[1].total);
  };

  const renderHotNumbersTable = () => {
    if (viewTabular) {
      // Tabular view - grouped by first digit
      const grouped = getGroupedCombinations();

      return (
        <div id='hot-numbers-table' className='mt-4'>
          <div className='bg-blue-100 p-3 rounded-t-lg'>
            <h2 className='text-center font-bold uppercase text-xs'>
              BET TABULATION
            </h2>
            <p className='text-center text-xs'>
              Draw Date/Time:{' '}
              {format(new Date(filters.drawDate), 'EEE, MMM dd, yyyy')}{' '}
              {filters.drawTime}
            </p>
          </div>

          <div className='bg-white overflow-x-auto flex items-start justify-start'>
            {Object.keys(grouped).map((digit) => (
              <table key={digit} className='text-sm text-left'>
                <thead className='text-sm uppercase bg-gray-50 border'>
                  <tr>
                    <th scope='col' className='py-1 px-1.5 font-bold border'>
                      {digit}
                    </th>
                    <th scope='col' className='py-1 px-2 font-medium border'>
                      T
                    </th>
                    <th
                      scope='col'
                      className='py-1 px-2 border-r font-medium border'
                    >
                      R
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {grouped[digit].map((row) => (
                    <tr key={row.combination} className='text-xs bg-white'>
                      <td className='border font-bold py-1 px-2'>
                        {row.combination}
                      </td>
                      <td className='py-1 px-2 border'>
                        {row.target.toFixed(2)}
                      </td>
                      <td className='py-1 px-2 border'>
                        {row.ramble.toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ))}
          </div>
        </div>
      );
    } else {
      // Regular table view
      const bets = getBetsArray();

      return (
        <div id='hot-numbers-table' className='mt-4'>
          <div className='bg-blue-100 p-3 rounded-t-lg'>
            <h2 className='text-center font-bold uppercase text-xs'>
              BET TABULATION
            </h2>
            <p className='text-center text-xs'>
              Draw Date/Time:{' '}
              {format(new Date(filters.drawDate), 'EEE, MMM dd, yyyy')}{' '}
              {filters.drawTime}
            </p>
          </div>

          <div className='bg-white overflow-x-auto'>
            <table className='w-full text-sm text-left text-gray-500'>
              <thead className='text-xs text-gray-700 uppercase bg-gray-50'>
                <tr>
                  <th scope='col' className='px-3 py-3'>
                    Combi
                  </th>
                  <th scope='col' className='px-3 py-3'>
                    Total
                  </th>
                  <th scope='col' className='px-3 py-3'>
                    Target
                  </th>
                  <th scope='col' className='px-3 py-3'>
                    Ramble
                  </th>
                </tr>
              </thead>
              <tbody>
                {bets.length > 0 ? (
                  bets.map((row) => (
                    <tr
                      key={row[1].combination}
                      className='bg-white border-b font-medium text-xs'
                    >
                      <th
                        scope='row'
                        className='px-3 py-4 text-gray-900 whitespace-nowrap font-bold'
                      >
                        {row[1].combination}
                      </th>
                      <td className='px-3 py-4'>₱{row[1].total.toFixed(2)}</td>
                      <td className='px-3 py-4 text-center'>
                        ₱{row[1].target.toFixed(2)}
                      </td>
                      <td className='px-3 py-4 text-center'>
                        ₱{row[1].ramble.toFixed(2)}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan='4' className='py-4 text-center text-gray-500'>
                      No hot numbers data found for the selected criteria
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      );
    }
  };

  return (
    <div className='bg-[#F8F9FB] min-h-screen pb-24'>
      <HeaderWithIcon title='Hot Numbers' onBack={() => navigate(-1)} />

      <div className='px-4 py-3'>
        <div className='bg-gray-50 rounded-lg p-4'>
          <div className='grid grid-cols-2 gap-4 mb-4'>
            <div>
              <label
                htmlFor='drawDate'
                className='block text-sm font-medium text-gray-700 mb-1'
              >
                Draw date
              </label>
              <div className='relative'>
                <input
                  type='date'
                  id='drawDate'
                  value={filters.drawDate}
                  onChange={handleDateChange}
                  className='bg-white border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5'
                />
              </div>
            </div>

            <div>
              <label
                htmlFor='gameId'
                className='block text-sm font-medium text-gray-700 mb-1'
              >
                Select Game
              </label>
              <Select
                id='gameId'
                name='gameId'
                value={filters.gameId}
                onChange={handleInputChange}
              >
                {games.map((game) => (
                  <option key={game.id} value={game.id}>
                    {game.name}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          <div className='grid grid-cols-2 gap-4 mb-4'>
            <div>
              <label
                htmlFor='drawTime'
                className='block text-sm font-medium text-gray-700 mb-1'
              >
                Select Draw Time
              </label>
              <Select
                id='drawTime'
                name='drawTime'
                value={filters.drawTime}
                onChange={handleInputChange}
              >
                {drawTimes.map((time) => (
                  <option key={time} value={time}>
                    {time}
                  </option>
                ))}
              </Select>
            </div>

            <div>
              <label
                htmlFor='combination'
                className='block text-sm font-medium text-gray-700 mb-1'
              >
                Combination
              </label>
              <TextInput
                id='combination'
                name='combination'
                value={filters.combination}
                onChange={handleInputChange}
                placeholder='Enter combination'
              />
            </div>
          </div>

          <div className='flex justify-between gap-2'>
            <Button color='blue' onClick={handleFilter} className='flex-1'>
              <Filter className='mr-2 h-5 w-5' />
              Filter
            </Button>

            <Button
              color='blue'
              onClick={() => setViewTabular(!viewTabular)}
              className='flex-1'
            >
              <FileText className='mr-2 h-5 w-5' />
              {viewTabular ? 'View Table' : 'View Tabular'}
            </Button>

            <Dropdown
              label={
                <div className='flex items-center'>
                  <Download className='mr-2 h-5 w-5' />
                  Download
                </div>
              }
              color='blue'
              className='flex-1'
            >
              <Dropdown.Item onClick={downloadPDF}>PDF</Dropdown.Item>
              <Dropdown.Item onClick={downloadCSV}>Excel (CSV)</Dropdown.Item>
            </Dropdown>
          </div>

          {dataLoadedAtFormatted && (
            <p className='mt-3 text-xs text-gray-500 text-right'>
              Data loaded: {dataLoadedAtFormatted}
            </p>
          )}
        </div>

        {loading ? (
          <div className='text-center py-8'>
            <div className='inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent'></div>
            <p className='mt-2 text-gray-600'>Loading hot numbers...</p>
          </div>
        ) : error ? (
          <div className='text-center py-8 text-red-500'>{error}</div>
        ) : (
          showResults && renderHotNumbersTable()
        )}
      </div>
    </div>
  );
}
